// src/index.js
import React, { Component } from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import "./index.css";
import { AuthProvider } from "react-oidc-context";
import cognitoAuthConfig from "./config/authConfig";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import faviconSvg from "./assets/ks-fav.svg";
import favicon16 from "./assets/ks-fav-16.png";
import favicon32 from "./assets/ks-fav-32.png";
import favicon48 from "./assets/ks-fav-48.png";

// ------------------------- Critical Error Boundary -------------------------
class CriticalErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("Critical error:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "20vh",
            fontFamily: "Manrope, Arial, sans-serif",
          }}
        >
          <h2>Something went wrong.</h2>
          <a href="/" style={{ color: "#09b2f0" }}>
            ← Back to home
          </a>
        </div>
      );
    }
    return this.props.children;
  }
}

// 🎨 Custom MUI theme with global font & iOS sticky hover fix at theme engine layer
//🎨 Production-Grade MUI theme with baseline hover suppression for iOS touch devices
// 🎨 Production-Grade theme config: Cleans up hover states for mobile viewports
const theme = createTheme({
  palette: {
    background: {
      default: "white",
    },
  },
  typography: {
    fontFamily: '"Manrope", Arial, sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 700, textTransform: "none" },
  },
  components: {
    MuiButton: {
      defaultProps: {
        component: "button", // Reverted back to native button tag to stop style leakage
      },
      styleOverrides: {
        root: ({ ownerState, theme: muiTheme }) => {
          const buttonColor = ownerState.color || "primary";
          const hoverBg =
            muiTheme.palette[buttonColor]?.dark ||
            muiTheme.palette.primary.dark;

          return {
            WebkitTapHighlightColor: "transparent",
            userSelect: "none",

            // 🖥️ Only apply a hover shift color if a physical mouse is used
            "@media (hover: hover)": {
              "&:hover": {
                backgroundColor:
                  ownerState.variant === "contained"
                    ? hoverBg
                    : "rgba(0, 0, 0, 0.04)",
              },
            },

            // 📱 On mobile devices, turn off the hover state completely
            "@media (hover: none)": {
              "&:hover": {
                // By matching the variant style, we ensure it never flashes transparent
                backgroundColor:
                  ownerState.variant === "contained"
                    ? muiTheme.palette[buttonColor]?.main ||
                      muiTheme.palette.primary.main
                    : "transparent",
              },
              "&:focus, &:focus-visible, &:active": {
                outline: "none",
                boxShadow: "none",
              },
            },
          };
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        component: "button",
      },
      styleOverrides: {
        root: {
          WebkitTapHighlightColor: "transparent",
          "@media (hover: hover)": {
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          },
          "@media (hover: none)": {
            "&:hover": {
              backgroundColor: "transparent",
            },
          },
        },
      },
    },
  },
});

// Create a single query client instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
    },
    mutations: {
      retry: 0,
      throwOnError: true,
    },
  },
});

// Dynamically set favicons in <head>
const setFavicons = (icons) => {
  icons.forEach(({ href, size, type }) => {
    const link = document.createElement("link");
    link.rel = "icon";
    link.type = type;
    if (size) link.sizes = size;
    link.href = href;
    document.head.appendChild(link);
  });
};

setFavicons([
  // SVG first (scalable); PNGs are fallbacks for browsers without SVG icon support
  { href: faviconSvg, type: "image/svg+xml" },
  { href: favicon16, size: "16x16", type: "image/png" },
  { href: favicon32, size: "32x32", type: "image/png" },
  { href: favicon48, size: "48x48", type: "image/png" },
]);

// Render React app
const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <React.StrictMode>
    <CriticalErrorBoundary>
      <AuthProvider {...cognitoAuthConfig}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <App />
          </ThemeProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </AuthProvider>
    </CriticalErrorBoundary>
  </React.StrictMode>,
);
